import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {Add as AddIcon, Search as SearchIcon} from "@mui/icons-material";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {formatReais} from "../../shared/utils/formatters";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {SistemaLogo} from "../Sistemas/components/SistemaVisual";
import PlanosLayout from "./components/PlanosLayout";
import NovoPlanoDialog from "./components/NovoPlanoDialog";
import {listPlanos, listSistemas, listVersoes} from "./PlanosComerciaisService";
import {
    agruparPorSistema,
    ehSistemaInterno,
    getMeioLabel,
    getModalidadeLabel,
    getSituacaoVersao,
    plural,
    rascunhoDe,
    versaoPrincipal
} from "./planoVersaoUtils";

const celula = {px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', verticalAlign: 'top', fontSize: 14};

// Quantas assinaturas cada plano tem, somando as versões. Chega depois da lista e, se falhar, a coluna fica sem número.
const useAssinaturasDosPlanos = (planos) => {
    const [contagem, setContagem] = useState({});
    useEffect(() => {
        let ativo = true;
        const fila = [...planos];
        const trabalhar = async () => {
            while (fila.length && ativo) {
                const plano = fila.shift();
                try {
                    const versoes = (await listVersoes(plano.id))?.data?.valor?.versoes || [];
                    if (ativo) setContagem((atual) => ({...atual, [plano.id]: versoes.reduce((total, versao) => total + (versao.assinaturas || 0), 0)}));
                } catch {
                    // a coluna fica sem o número
                }
            }
        };
        // Quatro de cada vez: cada plano é uma chamada.
        Promise.all([trabalhar(), trabalhar(), trabalhar(), trabalhar()]);
        return () => {
            ativo = false;
        };
    }, [planos]);
    return contagem;
};

const LinhaDoPlano = ({plano, assinaturas}) => {
    const navigate = useNavigate();
    const principal = versaoPrincipal(plano.versoes || []);
    const rascunho = rascunhoDe(plano.versoes || []);
    const situacao = getSituacaoVersao(principal?.situacao);
    const destino = `/planos-comerciais/${plano.id}${principal ? `/versoes/${principal.numero}` : ''}`;

    return (
        <Box component="tr" onClick={() => navigate(destino)} sx={{cursor: 'pointer', '&:hover': {bgcolor: 'action.hover'}}}>
            <Box component="td" sx={celula}>
                <Typography component={RouterLink} to={destino} onClick={(event) => event.stopPropagation()} variant="body2" fontWeight={700} sx={{color: 'text.primary', textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}>
                    {plano.nome_plano}
                </Typography>
                {plano.descricao ? <Typography variant="caption" color="text.secondary" display="block">{plano.descricao}</Typography> : null}
            </Box>
            <Box component="td" sx={celula}>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {principal ? <Chip size="small" label={`v${principal.numero} · ${situacao.label}`} color={situacao.color} variant="outlined" /> : <Typography variant="body2" color="text.secondary">Sem versão</Typography>}
                    {rascunho && rascunho !== principal ? <Chip size="small" color="info" label={`v${rascunho.numero} em rascunho`} /> : null}
                </Stack>
            </Box>
            <Box component="td" sx={celula}>{getModalidadeLabel(principal?.modalidade)}</Box>
            <Box component="td" sx={celula}>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {(principal?.meios_aceitos || []).map((meio) => <Chip key={meio} size="small" variant="outlined" label={getMeioLabel(meio)} />)}
                </Stack>
            </Box>
            <Box component="td" sx={{...celula, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'}}>{principal ? formatReais(principal.preco_base_mensal) : '-'}</Box>
            <Box component="td" sx={{...celula, whiteSpace: 'nowrap'}}>{principal?.dias_trial ? plural(principal.dias_trial, 'dia', 'dias') : '—'}</Box>
            <Box component="td" sx={{...celula, textAlign: 'right'}}>{assinaturas ?? '…'}</Box>
            <Box component="td" sx={celula}>{plano.vitrine ? <Chip size="small" color="primary" label="Na vitrine" /> : <Typography variant="body2" color="text.secondary">Não</Typography>}</Box>
        </Box>
    );
};

/*
 * Planos por sistema, com a versão principal de cada um (BBF-107, protótipo v2 "Planos"). A versão publicada não muda:
 * para mudar, cria-se um rascunho na página do plano. Sistema de uso interno não aparece aqui.
 */
export default function PlanosComerciaisPage({openParent = true}) {
    const navigate = useNavigate();
    const [estado, setEstado] = useState({carregando: true, erro: '', planos: [], sistemas: []});
    const [busca, setBusca] = useState('');
    const [sistemaFiltro, setSistemaFiltro] = useState('');
    const [novoAberto, setNovoAberto] = useState(false);

    const carregar = useCallback(async () => {
        setEstado((atual) => ({...atual, carregando: true, erro: ''}));
        try {
            const [planos, sistemas] = await Promise.all([listPlanos(), listSistemas()]);
            setEstado({carregando: false, erro: '', planos, sistemas});
        } catch (error) {
            setEstado({carregando: false, erro: getBackendErrorMessage(error, 'Não foi possível carregar os planos.'), planos: [], sistemas: []});
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const vendaveis = useMemo(() => estado.sistemas.filter((sistema) => !ehSistemaInterno(sistema)), [estado.sistemas]);
    const planosVisiveis = useMemo(() => estado.planos.filter((plano) => !ehSistemaInterno(plano.sistema)), [estado.planos]);
    const assinaturas = useAssinaturasDosPlanos(planosVisiveis);

    const filtrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return planosVisiveis.filter((plano) => (!sistemaFiltro || plano.id_sistema === sistemaFiltro)
            && (!termo || `${plano.nome_plano} ${plano.descricao || ''} ${plano.sistema?.nome || ''}`.toLowerCase().includes(termo)));
    }, [busca, planosVisiveis, sistemaFiltro]);
    const grupos = useMemo(() => agruparPorSistema(filtrados), [filtrados]);

    return (
        <PlanosLayout openParent={openParent}>
            <Stack spacing={3}>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2} justifyContent="space-between" alignItems={{md: 'center'}}>
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight={700}>Planos</Typography>
                        <Typography variant="body1" color="text.secondary">
                            Oferta comercial de cada sistema. O plano tem versões: a publicada não muda, e quem já assina fica na versão em que entrou.
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNovoAberto(true)} sx={{flexShrink: 0}}>Novo plano</Button>
                </Stack>

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                    <TextField
                        size="small"
                        placeholder="Buscar por plano, descrição ou sistema"
                        value={busca}
                        onChange={(event) => setBusca(event.target.value)}
                        inputProps={{'aria-label': 'Buscar planos'}}
                        InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>}}
                        sx={{flex: 1, maxWidth: {sm: 420}}}
                    />
                    <TextField select size="small" value={sistemaFiltro} onChange={(event) => setSistemaFiltro(event.target.value)} sx={{minWidth: 220}}
                               SelectProps={{displayEmpty: true, inputProps: {'aria-label': 'Filtrar por sistema'}}}>
                        <MenuItem value="">Todos os sistemas</MenuItem>
                        {vendaveis.map((sistema) => <MenuItem key={sistema.id} value={sistema.id}>{sistema.nome}</MenuItem>)}
                    </TextField>
                </Stack>

                {estado.erro ? <Alert severity="error">{estado.erro}</Alert> : null}

                {estado.carregando ? (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{py: 6}}>
                        <CircularProgress size={20} />
                        <Typography color="text.secondary">Carregando os planos…</Typography>
                    </Stack>
                ) : (
                    <Paper variant="outlined" sx={{overflow: 'hidden'}}>
                        <Box sx={{overflowX: 'auto'}}>
                            <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', minWidth: 900}}>
                                <Box component="thead">
                                    <Box component="tr" sx={{'& th': {px: 2, py: 1.25, textAlign: 'left', color: 'text.secondary', fontWeight: 500, fontSize: 13}}}>
                                        <th>Plano</th>
                                        <th>Versão principal</th>
                                        <th>Modalidade</th>
                                        <th>Meios</th>
                                        <Box component="th" sx={{textAlign: 'right !important'}}>Mensalidade</Box>
                                        <th>Trial</th>
                                        <Box component="th" sx={{textAlign: 'right !important'}}>Assinaturas</Box>
                                        <th>Vitrine</th>
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {grupos.map(({sistema, planos}) => (
                                        <React.Fragment key={sistema.id}>
                                            <Box component="tr" sx={{bgcolor: 'action.hover'}}>
                                                <Box component="td" colSpan={8} sx={{...celula, py: 1}}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <SistemaLogo sistema={sistema} tamanho={24} />
                                                        <Typography variant="body2" fontWeight={700}>{sistema.nome}</Typography>
                                                        {sistema.status === 'DESENVOLVIMENTO' ? <Chip size="small" color="warning" variant="outlined" label="Em desenvolvimento: publica depois de ir à venda" /> : null}
                                                    </Stack>
                                                </Box>
                                            </Box>
                                            {planos.map((plano) => <LinhaDoPlano key={plano.id} plano={plano} assinaturas={assinaturas[plano.id]} />)}
                                        </React.Fragment>
                                    ))}
                                    {!grupos.length ? (
                                        <Box component="tr">
                                            <Box component="td" colSpan={8} sx={{...celula, py: 5, textAlign: 'center', color: 'text.secondary'}}>
                                                {planosVisiveis.length ? 'Nenhum plano com esses filtros.' : 'Nenhum plano ainda. Crie o primeiro em Novo plano.'}
                                            </Box>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Stack>

            <NovoPlanoDialog
                aberto={novoAberto}
                sistemas={vendaveis}
                sistemaInicial={sistemaFiltro}
                onFechar={() => setNovoAberto(false)}
                onCriado={(plano) => {
                    setNovoAberto(false);
                    if (plano?.id) navigate(`/planos-comerciais/${plano.id}/versoes/1`);
                    else carregar();
                }}
            />
        </PlanosLayout>
    );
}
